/**
 * KYC Context - Global KYC State Management
 * Manages KYC verification status and modal state across the application
 */

import React, { createContext, useContext } from "react";
import { useKYC as useKYCHook } from "../shared/hooks/useKYC";

const KYCContext = createContext();

export const useKYC = () => {
  const context = useContext(KYCContext);
  if (!context) {
    throw new Error("useKYC must be used within a KYCProvider");
  }
  return context;
};

// Stable fallback used when the underlying KYC hook throws during render.
// Defined at module scope (not inside the component) so the object identity
// is stable across renders — prevents thrash in dependent useMemo/useEffect.
const KYC_FALLBACK = {
  kycStatus: null,
  isVerified: false,
  canRetry: true,
  loading: false,
  error: "KYC service unavailable",
  rejectionReason: null,
  fetchKYCStatus: () => {},
  checkKYCRequired: () =>
    Promise.resolve({ kycRequired: false, kycVerified: false }),
  requireKYCForAction: () => Promise.resolve(true),
  getStatusDisplay: () => null,
};

export const KYCProvider = ({ children }) => {
  // The previous version wrapped useKYCHook() in try/catch to provide a
  // fallback if the hook threw. That violates the rules of hooks (a hook
  // must be called unconditionally on every render path). Push error
  // handling INSIDE the hook itself; here we just call it. If it ever
  // throws synchronously the nearest error boundary catches it and the
  // fallback object above is the contract for what consumers should
  // render against.
  const kycData = useKYCHook();
  return <KYCContext.Provider value={kycData}>{children}</KYCContext.Provider>;
};

// Exported so consumers / tests / error boundaries can render against the
// same shape the hook would have produced on failure.
export { KYC_FALLBACK };

export default KYCContext;
